import { REFERRAL_TYPES } from '../../../shared/constants/paroReferrals'
import { DEGREE_TYPES } from '../../../shared/constants/educationLevel'
import {
  ANNUAL_COMPANY_REVENUE_RANGE,
  COMPANY_REVENUE_MODEL,
  FUNDING_SOURCE,
  COMPANY_SIZE,
  COMPANY_TYPE,
  PROJECT_SIZE_ITEMS,
  PROJECT_SIZE,
} from '../../../shared/constants/projects'

import {
  weeklyWorkloadHours,
  paroIncomeStatus,
  totalFreelanceExperience,
  projectTypes,
  contactTimePreferences,
  communicationMethods,
} from '../../../shared/constants/workPreferences'

import { formatCurrency } from '../../../shared/helpers/currency'

const getContactInfoFields = (field) => {
  const name = field[0]
  const value = field[1]

  const referralType = REFERRAL_TYPES.find((item) => item.value === value)

  switch (name) {
    case 'address1':
      return { label: 'Address Line 1', value }
    case 'address2':
      return { label: 'Address Line 2', value }
    case 'zipCode':
      return { label: 'Zip Code', value }
    case 'region':
      return { label: 'State', value }
    case 'city':
      return { label: 'City', value }
    case 'phone':
      return { label: 'Phone Number', value }
    case 'paroReferrer':
      return { label: 'How did you hear about Paro?', value: referralType?.label }
  }
}

const getEducationFields = (field) => {
  const name = field[0]
  const value = field[1]

  const degreeType = DEGREE_TYPES.find((item) => item.value === value)

  switch (name) {
    case 'schoolName':
      return { label: 'School Name', value }
    case 'endDate':
      return { label: 'Year Graduated', value }
    case 'startDate':
      return { label: 'Year Enrolled', value }
    case 'major':
      return { label: 'Major', value }
    case 'degree':
      return { label: 'Degree', value: degreeType?.label }
  }
}

const getWorkExperienceFields = (field) => {
  const name = field[0]
  const value = field[1]

  switch (name) {
    case 'title':
      return { label: 'Title', value }
    case 'employer':
      return { label: 'Employer', value }
    case 'endDate':
      return { label: 'End Date', value }
    case 'startDate':
      return { label: 'Start Date', value }
    case 'description':
      return { label: 'Description', value }
    case 'industryTags':
      return { label: 'Industry', value }
    case 'isCurrentEmployer':
      return { label: 'Current Employer?', value: value ? 'Yes' : 'No' }
    case 'financialTags':
      return { label: 'Skills Used', value }
    case 'softwareTags':
      return { label: 'Software Used', value }
  }
}

const getProjectFields = (field) => {
  const name = field[0]
  const value = field[1]

  const annualRevenueType = ANNUAL_COMPANY_REVENUE_RANGE.find((item) => item.value === value)
  const companySizeType = COMPANY_SIZE.find((item) => item.value === value)
  const companyRevenueType = COMPANY_REVENUE_MODEL.find((item) => item.value === value)
  const companyType = COMPANY_TYPE.find((item) => item.value === value)
  const fundingSourcesType = FUNDING_SOURCE.find((item) => item.value === value)
  const projectSize = PROJECT_SIZE.find((item) => item.value.hoursMonthly === value || item.value.hoursTotal === value)
  const projectSizeType = PROJECT_SIZE_ITEMS.find((item) => item.value === projectSize?.id)
  switch (name) {
    case 'annualCompanyRevenueRange':
      return { label: 'Annual Revenue', value: annualRevenueType?.label }
    case 'companyRevenueModel':
      return { label: 'Revenue Model', value: companyRevenueType?.label }
    case 'companySize':
      return { label: 'Employees', value: companySizeType?.label }
    case 'companyType':
      return { label: 'Entity Type', value: companyType?.label }
    case 'description':
      return { label: 'Description', value }
    case 'endDate':
      return { label: 'End Date', value }
    case 'startDate':
      return { label: 'Start Date', value }
    case 'fundingSources':
      return { label: 'Funding Source', value: fundingSourcesType?.label }
    case 'isInternational':
      return { label: 'International Business?', value: value ? 'Yes' : 'No' }
    case 'onsite':
      return { label: 'Onsite', value: value ? 'Yes' : 'No' }
    case 'projectSize':
      return { label: 'Project Size', value: projectSizeType?.label }
    case 'title':
      return { label: 'Title', value }
    case 'industryTags':
      return { label: 'Industry', value }
    case 'financialTags':
      return { label: 'Skills Used', value }
    case 'softwareTags':
      return { label: 'Software Used', value }
  }
}

const getWorkPreferenceFields = (field) => {
  const name = field[0]
  const value = field[1]

  const weeklyHours = weeklyWorkloadHours.find((item) => item.value === value)
  const projectType = projectTypes.find((item) => item.value === value)
  const communicationMethod = communicationMethods.find((item) => item.value === value)
  const contactTimePreference = contactTimePreferences.find((item) => item.value === value)
  const freelancerExperience = totalFreelanceExperience.find((item) => item.value === value)
  const incomeStatus = paroIncomeStatus.find((item) => item.value === value)

  switch (name) {
    case 'contactStylePreference':
      return { label: 'What is the best method of communication?', value: communicationMethod?.label }
    case 'contactTimePreference':
      return { label: 'When are you available to talk to clients?', value: contactTimePreference?.label }
    case 'paroIncomeStatus':
      return { label: 'Best fit', value: incomeStatus?.label }
    case 'hasCurrentClients':
      return { label: 'Do you have an existing book of business?', value }
    case 'isOpenToOnsite':
      return { label: 'Are you willing to go onsite?', value }
    case 'intendedMonthlyIncome':
      return { label: 'What is your monthly income goal with Paro?', value: formatCurrency(value) }
    case 'projectType':
      return { label: 'What type of projects are you willing to work on?', value: projectType?.label }
    case 'weeklyWorkloadHours':
      return { label: 'Weekly hourly goal', value: weeklyHours?.label }
    case 'totalFreelanceExperience':
      return { label: 'How long have you been freelancing?', value: freelancerExperience?.label }
  }
}

const getPitchFields = (field) => {
  const name = field[0]
  const value = field[1]

  switch (name) {
    case 'aboutYourself':
      return { label: 'About yourself', value }
    case 'topIndustries':
      return { label: 'Top Industries', value }
    case 'topFinancialSkills':
      return { label: 'Top Skills', value }
    case 'topSoftwareSkills':
      return { label: 'Top Software Skills', value }
    case 'whyFreelanceWork':
      return { label: 'Why do you want to do freelance work?', value }
  }
}

export {
  getContactInfoFields,
  getEducationFields,
  getWorkExperienceFields,
  getProjectFields,
  getWorkPreferenceFields,
  getPitchFields,
}
